import { useCallback, useState } from 'react';
import { type BaseRelationEditor, type WithId } from './useRelationEditor.ts';

export interface RelationActiveHandle<B, T = WithId<B>> {
  /** Avvia la modifica o la creazione di un elemento. Se è specificato un indice verrà inserito in quella posizione */
  open: (record: T, index?: number) => void;
  /** Annulla le modifiche */
  cancel: () => void;
  /** Applica le modifiche, se index è fornito e l'elemento è nuovo, verrà aggiunto in quella posizione, altrimenti sarà aggiunto in fondo */
  apply: (input: T, index?: number | null) => 'updated' | 'added';
  /** Rimuove l'elemento corrente */
  remove: () => 'removed';
  /** Elemento corrente */
  current: T | null;
  /** E' un nuovo elemento quello in modifica? */
  isNew: boolean;
  /** È visibile? */
  isVisible: boolean;
}

/**
 * Ritorna un handle per gestire la selezione di un elemento attivo della
 * relazione che si sta modificando.
 * Permette di gestire una Modal o un Drawer di modifica "inline"
 */
export function useRelationActiveHandle<T>(
  editor: BaseRelationEditor<T>,
): RelationActiveHandle<T> {
  const [current, setCurrent] = useState<WithId<T> | null>(null);
  const [atIndex, setAtIndex] = useState<number | null>(null);

  const isNew = current == null || !editor.items.includes(current);

  const open = useCallback((record: WithId<T>, index?: number) => {
    setCurrent(record);
    if (index) setAtIndex(index);
  }, []);

  const cancel = useCallback(() => {
    setCurrent(null);
    setAtIndex(null);
  }, []);

  /** Applica le modifiche all'elemento corrente (sia in update che in add) */
  const apply = useCallback(
    (input: WithId<T>, targetIndex?: number | null) => {
      // Necessario per il "reinitialize" di Formik
      setCurrent(input);

      const index = targetIndex ?? atIndex;

      if (!isNew) {
        editor.update(current, input);
        setCurrent(null);
        return 'updated';
      }
      if (index) {
        editor.insert(index, input);
      } else {
        editor.append(input);
      }
      setCurrent(null);
      setAtIndex(null);
      return 'added';
    },
    [atIndex, current, editor, isNew],
  );

  /** Elimina l'elemento corrente  */
  const remove = useCallback(() => {
    editor.remove(current!);
    setCurrent(null);

    return 'removed';
  }, [current, editor]);

  return {
    open,
    cancel,
    apply,
    remove,
    current,
    isNew: current == null || !editor.items.includes(current),
    isVisible: current != null,
  };
}
